import React, { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import { useUsers } from '../hooks/useUsers';
import { auth } from '../utils/firebase';
import { UserProfile } from '../types/user.types';
import CreateUserModal from '../components/modals/CreateUserModal';
import EditUserModal from '../components/modals/EditUserModal';

const UserManagement: React.FC = () => {
  const { profile } = useAuth();
  const { loading, error, users, listUsers, deleteUser, revokeAllSessions } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      await listUsers();
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleDeactivate = async (uid: string, email: string) => {
    if (!window.confirm(`Are you sure you want to deactivate ${email}?`)) {
      return;
    }

    setLoadingAction(uid);
    try {
      await deleteUser(uid);
      await loadUsers(); // Reload the list
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate user');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRevokeAllSessions = async (uid: string, email: string) => {
    if (!window.confirm(`Logout ${email} from all devices?`)) {
      return;
    }

    setLoadingAction(uid);
    try {
      await revokeAllSessions(uid);
      alert('All sessions revoked successfully');
    } catch (err: any) {
      alert(err.message || 'Failed to revoke sessions');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSendPasswordReset = async (uid: string, email: string) => {
    if (!window.confirm(`Send a password reset email to ${email}?`)) {
      return;
    }

    setLoadingAction(uid);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      alert(`Password reset email sent to ${email}`);
    } catch (err: any) {
      alert(err.message || 'Failed to send password reset email');
    } finally {
      setLoadingAction(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.displayName?.toLowerCase().includes(searchLower) ||
      user.role.toLowerCase().includes(searchLower)
    );
  });

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superAdmin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header - Compacted */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">User Management</h1>
        <p className="text-sm text-gray-600">Manage admin users and their permissions</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Actions Bar - Compacted */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        {/* Search */}
        <div className="w-full sm:w-96">
          <input
            type="text"
            placeholder="Search users by email, name, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Create User Button (SuperAdmin only) */}
        {profile?.role === 'superAdmin' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            + Create New User
          </button>
        )}
      </div>

      {/* Users Table - Compacted */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm text-gray-600">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                    {/* User Info */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-xs">
                            {user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {user.displayName || 'No Name'}
                          </div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getRoleBadgeColor(
                          user.role
                        )}`}
                      >
                        {user.role}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeColor(
                          user.isActive
                        )}`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {!user.emailVerified && (
                        <span className="ml-2 px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Email Not Verified
                        </span>
                      )}
                    </td>

                    {/* Created Date */}
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>

                    {/* Last Login */}
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(user.lastLogin)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-2.5 whitespace-nowrap text-right text-xs font-medium">
                      <div className="flex justify-end gap-2">
                        {/* Edit Button */}
                        <button
                          onClick={() => setEditingUser(user)}
                          disabled={loadingAction === user.uid}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        >
                          Edit
                        </button>

                        {/* Revoke Sessions Button */}
                        {user.isActive && (
                          <button
                            onClick={() => handleRevokeAllSessions(user.uid, user.email)}
                            disabled={loadingAction === user.uid}
                            className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                          >
                            Logout All
                          </button>
                        )}

                        {/* Password Reset Button */}
                        {user.isActive && (
                          <button
                            onClick={() => handleSendPasswordReset(user.uid, user.email)}
                            disabled={loadingAction === user.uid}
                            className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                          >
                            Reset Password
                          </button>
                        )}

                        {/* Deactivate Button */}
                        {user.isActive && user.uid !== profile?.uid && (
                          <button
                            onClick={() => handleDeactivate(user.uid, user.email)}
                            disabled={loadingAction === user.uid}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            {loadingAction === user.uid ? 'Processing...' : 'Deactivate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats Summary - Compacted */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Total Users</p>
          <p className="text-xl font-bold text-gray-900 mt-0.5">{users.length}</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Active Users</p>
          <p className="text-xl font-bold text-green-600 mt-0.5">
            {users.filter((u) => u.isActive).length}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">SuperAdmins</p>
          <p className="text-xl font-bold text-red-600 mt-0.5">
            {users.filter((u) => u.role === 'superAdmin').length}
          </p>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
